import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Upload, Sparkles, MessagesSquare, Layers, ClipboardCheck, Target,
    GraduationCap, Check, ChevronRight, AlertTriangle, ArrowRight, FileText,
    Timer, AlarmClock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";

const STEP_META = [
    { key: "upload", icon: Upload },
    { key: "summary", icon: Sparkles },
    { key: "questions", icon: MessagesSquare },
    { key: "flashcards", icon: Layers },
    { key: "quiz", icon: ClipboardCheck },
    { key: "exam", icon: Timer },
    { key: "gaps", icon: Target },
    { key: "coach", icon: GraduationCap },
];

const Frame = ({ title, children }) => (
    <div data-testid="product-tour-live-preview" className="overflow-hidden rounded-[var(--radius)] border border-border bg-surface-1 shadow-elev-2">
        <div className="flex items-center gap-2 border-b border-border bg-surface-2 px-4 py-2.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--destructive))]/55" />
            <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--warning))]/55" />
            <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--success))]/55" />
            <span className="ml-2 mono-label text-t4">LearnOS · {title}</span>
        </div>
        <div className="min-h-[320px] p-5 sm:p-6">{children}</div>
    </div>
);

const UploadPreview = ({ t }) => (
    <div className="space-y-4">
        <div className="rounded-xl border border-dashed border-border bg-surface-2 p-8 text-center">
            <Upload className="mx-auto h-6 w-6 text-[hsl(var(--primary))]" />
            <div className="mt-3 text-sm font-medium text-t1">{t("preview.upload.dragDrop")}</div>
            <div className="mono-label mt-1 text-t4">{t("preview.upload.formats")}</div>
        </div>
        <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-t2"><FileText className="h-4 w-4 text-[hsl(var(--primary))]" /> research-paper.pdf <span className="ml-auto inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]"><Check className="h-3.5 w-3.5" /> {t("preview.upload.parsed")}</span></div>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-t2"><FileText className="h-4 w-4 text-t4" /> lecture-12.pdf <span className="ml-auto text-xs text-t4">{t("preview.upload.parsing")}</span></div>
        </div>
    </div>
);

const SummaryPreview = ({ t }) => (
    <div className="space-y-3">
        <div className="mono-label text-t4">{t("preview.summary.title5")}</div>
        {t("preview.summary.tourBullets").map((b, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-t2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--success))]" />{b}</div>
        ))}
        <div className="flex flex-wrap gap-1.5 pt-1">{t("preview.summary.terms").map((tm) => <span key={tm} className="rounded-full border border-border bg-surface-2 px-2.5 py-1 text-xs text-t3">{tm}</span>)}</div>
        <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--primary))]"><MessagesSquare className="h-3.5 w-3.5" /> {t("preview.summary.ask")}</div>
    </div>
);

const ChatPreview = ({ t }) => (
    <div className="space-y-3">
        <div className="ml-auto max-w-[85%] rounded-2xl border border-border bg-surface-3 px-3.5 py-2.5 text-sm text-t1">{t("preview.chat.tourQ1")}</div>
        <div className="mr-auto max-w-[92%] rounded-2xl border border-border bg-surface-2 px-3.5 py-2.5 text-sm text-t2">{t("preview.chat.tourA1")} <span className="ml-1 inline-flex items-center rounded-full border border-border bg-surface-3 px-2 py-0.5 font-mono-ui text-[11px] text-t3">p. 24</span></div>
        <div className="ml-auto max-w-[85%] rounded-2xl border border-border bg-surface-3 px-3.5 py-2.5 text-sm text-t1">{t("preview.chat.tourQ2")}</div>
    </div>
);

const FlashcardPreview = ({ t }) => {
    const [flipped, setFlipped] = useState(false);
    return (
        <div className="mx-auto max-w-md">
            <div className="mb-3 flex items-center justify-between"><span className="mono-label text-t4">{t("preview.flashcard.card")} 3 / 18</span><span className="mono-label text-t4">{t("preview.flashcard.activeRecall")}</span></div>
            <button
                data-testid="tour-flashcard"
                onClick={() => setFlipped((f) => !f)}
                className="flex min-h-[170px] w-full items-center justify-center rounded-2xl border border-border bg-surface-2 p-6 text-center transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--focus))]"
            >
                <AnimatePresence mode="wait">
                    <motion.div key={flipped ? "b" : "f"} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                        <div className="mono-label mb-2 text-t4">{flipped ? t("preview.flashcard.answer") : t("preview.flashcard.question")}</div>
                        <div className="font-reading text-base leading-relaxed text-t1">{flipped ? t("preview.flashcard.a") : t("preview.flashcard.q")}</div>
                    </motion.div>
                </AnimatePresence>
            </button>
            <div className="mt-3 text-center text-xs text-[hsl(var(--primary))]">{flipped ? t("preview.flashcard.rate") : t("preview.flashcard.tapReveal")}</div>
        </div>
    );
};

const QuizPreview = ({ t }) => {
    const opts = t("preview.quiz.tourOptions");
    const correct = 1;
    const [sel, setSel] = useState(null);
    return (
        <div className="mx-auto max-w-md">
            <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-surface-2"><div className="h-full w-2/3 rounded-full bg-[hsl(var(--primary))]" /></div>
            <div className="mono-label mb-2 text-t4">{t("preview.quiz.question")} 2 / 3</div>
            <div className="text-sm font-medium text-t1">{t("preview.quiz.tourQ")}</div>
            <div className="mt-3 space-y-2">
                {opts.map((o, i) => {
                    const showState = sel !== null;
                    const isCorrect = i === correct;
                    const chosenWrong = sel === i && !isCorrect;
                    return (
                        <button
                            key={o}
                            data-testid={`tour-quiz-option-${i}`}
                            onClick={() => setSel(i)}
                            className={cn(
                                "flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--focus))]",
                                showState && isCorrect ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10 text-t1"
                                    : chosenWrong ? "border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 text-t2"
                                    : "border-border bg-surface-2 text-t2 hover:bg-surface-3"
                            )}
                        >
                            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-[11px]">{String.fromCharCode(65 + i)}</span>
                            {o}
                            {showState && isCorrect && <Check className="ml-auto h-4 w-4 text-[hsl(var(--success))]" />}
                        </button>
                    );
                })}
            </div>
            <div className="mt-3 text-xs text-t3">{sel === null ? t("preview.quiz.pick") : sel === correct ? t("preview.quiz.correct") : t("preview.quiz.wrong")}</div>
        </div>
    );
};

const ExamPreview = ({ t }) => {
    const opts = t("preview.exam.options");
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <span className="mono-label text-t4">{t("preview.exam.mock")}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-2.5 py-1 text-xs text-[hsl(var(--warning))]"><AlarmClock className="h-3.5 w-3.5" /> {t("preview.exam.timeLeft")} 12:48</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2"><div className="h-full w-1/5 rounded-full bg-[hsl(var(--primary))]" /></div>
            <div className="mono-label text-t4">{t("preview.exam.q")}</div>
            <div className="text-sm font-medium text-t1">{t("preview.exam.prompt")}</div>
            <div className="space-y-2">
                {opts.map((o, i) => (
                    <div key={o} className="flex items-center gap-2.5 rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-sm text-t2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-[11px]">{String.fromCharCode(65 + i)}</span>
                        {o}
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-between pt-1">
                <span className="mono-label text-t4">{t("preview.exam.graded")}</span>
                <span className="rounded-lg bg-[hsl(var(--primary))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--primary-foreground))]">{t("preview.exam.submit")}</span>
            </div>
        </div>
    );
};

const GapsPreview = ({ t }) => {
    const topics = t("preview.gaps.tourTopics");
    const vals = [34, 52, 61];
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-sm text-[hsl(var(--warning))]"><AlertTriangle className="h-4 w-4" /> 3 {t("preview.gaps.needMore")}</div>
            {topics.map((tp, i) => (
                <div key={tp}>
                    <div className="mb-1 flex items-center justify-between text-xs text-t3"><span className="text-t2">{tp}</span><span>{vals[i]}% {t("preview.gaps.confident")}</span></div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2"><div className="h-full rounded-full bg-[hsl(var(--warning))]" style={{ width: `${vals[i]}%` }} /></div>
                </div>
            ))}
            <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--primary))]"><ArrowRight className="h-3.5 w-3.5" /> {t("preview.gaps.reviewNow")}</div>
        </div>
    );
};

const CoachPreview = ({ t }) => (
    <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-t1"><GraduationCap className="h-4 w-4 text-[hsl(var(--primary))]" /> {t("preview.coach.yourPlan")}</div>
        {t("preview.coach.tourItems").map((it) => (
            <div key={it} className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-t2"><Check className="h-4 w-4 text-[hsl(var(--success))]" />{it}</div>
        ))}
        <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-3 py-2.5">
            <span className="text-sm text-t1">{t("preview.coach.streakLine")}</span>
            <ChevronRight className="h-4 w-4 text-[hsl(var(--primary))]" />
        </div>
    </div>
);

const Preview = ({ stepKey, t }) => {
    switch (stepKey) {
        case "upload": return <UploadPreview t={t} />;
        case "summary": return <SummaryPreview t={t} />;
        case "questions": return <ChatPreview t={t} />;
        case "flashcards": return <FlashcardPreview t={t} />;
        case "quiz": return <QuizPreview t={t} />;
        case "exam": return <ExamPreview t={t} />;
        case "gaps": return <GapsPreview t={t} />;
        case "coach": return <CoachPreview t={t} />;
        default: return null;
    }
};

export const InteractiveProductTour = () => {
    const { t } = useTranslation();
    const [activeIdx, setActiveIdx] = useState(0);
    const steps = t("landing.tour.steps");
    const active = STEP_META[activeIdx];
    const activeStep = steps[activeIdx] || {};

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-4">
                <div className="space-y-2">
                    {STEP_META.map((s, idx) => {
                        const on = idx === activeIdx;
                        const Icon = s.icon;
                        const meta = steps[idx] || {};
                        return (
                            <button
                                key={s.key}
                                data-testid={`product-tour-step-${s.key}`}
                                onClick={() => setActiveIdx(idx)}
                                aria-pressed={on}
                                className={cn(
                                    "flex w-full items-center gap-3 rounded-[var(--radius)] border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--focus))]",
                                    on ? "border-[hsl(var(--primary))]/55 bg-[hsl(var(--primary))]/10" : "border-border bg-surface-2 hover:bg-surface-3"
                                )}
                            >
                                <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", on ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-surface-3 text-t3")}>
                                    <Icon className="h-4 w-4" />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-medium text-t1">{idx + 1}. {meta.title}</span>
                                    <span className="block text-xs text-t3">{meta.caption}</span>
                                </span>
                                <ChevronRight className={cn("ml-auto h-4 w-4 shrink-0", on ? "text-[hsl(var(--primary))]" : "text-t4")} />
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="lg:col-span-8">
                <AnimatePresence mode="wait">
                    <motion.div key={active.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}>
                        <Frame title={activeStep.title}><Preview stepKey={active.key} t={t} /></Frame>
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};
